import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarBlank, ClipboardText, ClockCounterClockwise, EnvelopeSimple, FileText, Key, NotePencil, PencilSimple, Phone, Receipt, Ticket, TrendUp, UsersThree, UserSwitch, type Icon,
} from '@phosphor-icons/react';
import { Chip, ImmoButton, InnenKarte, KartenTitel, KpiReihe, KpiTile, LeerZustand, SegmentedControl } from '../../../../components/immobilien/ui';
import { useIslandActions } from '../../../../components/island/IslandProvider';
import { eur } from '../../../../lib/immobilien/format';
import { deDatum } from '../../../../lib/immobilien/objektDetail';
import { analyseMieterhoehung, type Kappung, type MietRoh } from '../../../../lib/immobilien/mieterhoehung';
import type { SchluesselStand } from '../../../../lib/immobilien/schluessel';
import type { Aktivitaet } from '../../../../lib/immobilien/aktivitaet';

// ── Schlüssel ────────────────────────────────────────────────────────────────

/**
 * „Schlüssel“: Stand aus dem letzten abgeschlossenen Übergabeprotokoll je Einheit.
 * Nach einem Einzug sind die Schlüssel beim Mieter, nach einem Auszug zurück.
 * Ein eigenes Schlüsselregister (Schließplan, Nachbestellungen, Verlust) fehlt noch.
 */
export function SchluesselBereich({ staende, einheiten, mieterName }: {
  staende: readonly SchluesselStand[];
  einheiten: readonly { id: string; bezeichnung: string }[];
  mieterName: (id: string | null) => string | null;
}) {
  const navigate = useNavigate();
  const mitStand = einheiten.filter(e => staende.some(s => s.einheitId === e.id));
  const beiMietern = staende.filter(s => s.art === 'einzug').reduce((n, s) => n + s.summe, 0);
  if (staende.length === 0) {
    return (
      <LeerZustand icon={Key} titel="Noch keine Schlüssel erfasst" text="Schlüssel werden im Übergabeprotokoll (Ein- und Auszug) gezählt und erscheinen dann hier.">
        <ImmoButton art="akzent" icon={ClipboardText} onClick={() => navigate('/admin/uebergabeprotokolle')}>Übergabeprotokoll</ImmoButton>
      </LeerZustand>
    );
  }
  return (
    <>
      {einheiten.length > 1 && (
        <KpiReihe>
          <KpiTile label="Bei Mietern" wert={beiMietern} sub="laut letztem Einzugsprotokoll" icon={Key} ton="acc" />
          <KpiTile label="Einheiten mit Protokoll" wert={`${mitStand.length} / ${einheiten.length}`} sub="Übergabe abgeschlossen" icon={ClipboardText} />
        </KpiReihe>
      )}
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(min(280px,100%),1fr))', gap: 12 }}>
        {einheiten.map(e => {
          const s = staende.find(x => x.einheitId === e.id);
          return (
            <InnenKarte key={e.id}>
              <KartenTitel icon={Key} rechts={s ? `${s.summe} Stück` : undefined}>{e.bezeichnung}</KartenTitel>
              {!s ? <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Kein abgeschlossenes Übergabeprotokoll.</span> : (
                <>
                  <span className="flex items-center flex-wrap" style={{ gap: 8, fontSize: 12, color: 'var(--ink3)' }}>
                    <Chip groesse="xs" ton={s.art === 'einzug' ? 'acc' : 'ok'}>{s.art === 'einzug' ? 'Beim Mieter' : 'Auszug erfasst'}</Chip>
                    {s.art === 'einzug' ? 'Einzug' : 'Auszug'} am {deDatum(s.datum)}{mieterName(s.mieterId) ? ` · ${mieterName(s.mieterId)}` : ''}
                  </span>
                  {s.summe === 0 ? <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Keine Schlüssel im Protokoll gezählt.</span> : (
                    <div className="flex flex-col">
                      {s.posten.filter(p => p.anzahl > 0).map((p, i) => (
                        <div key={`${p.bezeichnung}-${i}`} className="flex items-center justify-between" style={{ padding: '8px 0', borderBottom: '1px solid var(--line)', gap: 10 }}>
                          <span style={{ fontSize: 14 }}>{p.bezeichnung}</span>
                          <span className="immo-zahl" style={{ fontSize: 14, fontWeight: 800 }}>{p.anzahl}×</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </InnenKarte>
          );
        })}
      </div>
      <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Schließplan, Nachbestellungen und Verlustmeldungen werden noch angebunden.</span>
    </>
  );
}

// ── Aktivität ────────────────────────────────────────────────────────────────

const AKT_ICON: Record<string, Icon> = {
  notiz: NotePencil, dokument: FileText, ticket: Ticket, termin: CalendarBlank, aenderung: UserSwitch,
  email: EnvelopeSimple, anruf: Phone, meeting: UsersThree, rechnung: Receipt,
};
const TAG_ZEIT = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

/** „Aktivität“: Zeitleiste aus Notizen, Dokumenten, Tickets, Terminen, Übergaben und Mieteränderungen. */
const AKT_HINWEIS = 'Ein vollständiges Änderungsprotokoll je Objekt wird noch angebunden; Änderungen werden heute nur an Mietern und Kontakten mitgeschrieben.';

export function AktivitaetBereich({ aktivitaeten, hinweis = AKT_HINWEIS }: { aktivitaeten: readonly Aktivitaet[]; /** null = ohne Hinweis (Mieterseite) */ hinweis?: string | null }) {
  const navigate = useNavigate();
  const { onSearchNavigate } = useIslandActions();
  if (aktivitaeten.length === 0) return <LeerZustand icon={ClockCounterClockwise} titel="Noch keine Aktivitäten" />;
  const oeffne = (a: Aktivitaet) => {
    if (a.ziel?.typ === 'ticket') navigate(`/admin/tickets/${encodeURIComponent(a.ziel.id)}`);
    else if (a.ziel?.typ === 'termin') onSearchNavigate?.('/admin/kalender', a.ziel.id, 'termin');
  };
  return (
    <InnenKarte>
      <div className="relative flex flex-col" style={{ gap: 2, paddingLeft: 4 }}>
        {aktivitaeten.map((a, i) => {
          const IconKomp = AKT_ICON[a.typ] ?? ClockCounterClockwise;
          const klickbar = !!a.ziel;
          const d = new Date(a.datum);
          return (
            <button key={`${a.id ?? i}-${a.datum}`} type="button" disabled={!klickbar} onClick={() => oeffne(a)}
              className={`flex items-start text-left ${klickbar ? 'immo-hover-flaeche cursor-pointer' : 'cursor-default'}`}
              style={{ gap: 12, padding: '10px 8px', borderRadius: 12, border: 'none', background: 'transparent', color: 'var(--ink)' }}>
              <span className="flex items-center justify-center shrink-0" style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--fill2)' }}><IconKomp weight="fill" size={15} color="var(--acc)" /></span>
              <span className="flex flex-col flex-1 min-w-0" style={{ gap: 2 }}>
                <span style={{ fontSize: 14, fontWeight: 700, overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{a.titel}</span>
                {a.detail && <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{a.detail}</span>}
              </span>
              <span className="immo-zahl shrink-0" style={{ fontSize: 12, color: 'var(--ink3)' }}>{Number.isNaN(d.getTime()) ? '' : TAG_ZEIT.format(d)}</span>
            </button>
          );
        })}
      </div>
      {hinweis && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{hinweis}</span>}
    </InnenKarte>
  );
}

// ── Mieterhöhung ─────────────────────────────────────────────────────────────

export interface MieterhoehungZeile { id: string; einheit: string; mieter: string; flaeche: number; roh: MietRoh; onOeffnen?: () => void; onBearbeiten?: () => void }

export function KappungsWahl({ wert, onChange }: { wert: Kappung; onChange: (k: Kappung) => void }) {
  return (
    <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
      <SegmentedControl hoehe={34} ariaLabel="Kappungsgrenze" wert={String(wert) as '20' | '15'} onChange={v => onChange(v === '15' ? 15 : 20)}
        segmente={[{ key: '20', label: 'Kappung 20 %' }, { key: '15', label: '15 % (angespannter Markt)' }]} />
      <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Ob 15 % gelten, legt die Landesverordnung für die Gemeinde fest.</span>
    </div>
  );
}

/**
 * „Mieterhöhung“ (§ 558 BGB): frühester Zeitpunkt und Spielraum nach der Kappungsgrenze je
 * Mietverhältnis. Der Mietspiegel fehlt – ob die Vergleichsmiete den Spielraum trägt, muss
 * noch geprüft werden (Fehlende Daten).
 */
export function MieterhoehungBereich({ zeilen, heute, einzeln = false }: { zeilen: readonly MieterhoehungZeile[]; heute: string; einzeln?: boolean }) {
  const [kappung, setKappung] = useState<Kappung>(20);
  if (zeilen.length === 0) return <LeerZustand icon={TrendUp} titel="Keine vermieteten Wohnungen" text="Die Mieterhöhung nach § 558 BGB gilt nur für Wohnraum, nicht für Garagen, Stellplätze oder Gewerbe." />;
  const analysen = zeilen.map(z => ({ z, a: analyseMieterhoehung(z.roh, heute, z.flaeche, kappung) }));
  const jetzt = analysen.filter(x => x.a.fruehestesVerlangen <= heute && x.a.spielraum > 0 && !x.a.geplanteAenderung);
  const summe = jetzt.reduce((s, x) => s + x.a.spielraum, 0);
  return (
    <>
      <KappungsWahl wert={kappung} onChange={setKappung} />
      {!einzeln && (
        <KpiReihe>
          <KpiTile label="Jetzt möglich" wert={`${jetzt.length} / ${zeilen.length}`} sub="Mietverhältnisse ohne Sperrfrist" icon={TrendUp} ton="acc" />
          <KpiTile label="Spielraum gesamt" wert={eur(summe)} sub="pro Monat, höchstens (Kappungsgrenze)" ton={summe > 0 ? 'ok' : 'neutral'} wertFarbig={summe > 0} />
        </KpiReihe>
      )}
      {analysen.map(({ z, a }) => {
        const moeglich = a.fruehestesVerlangen <= heute;
        return (
          <InnenKarte key={z.id}>
            <KartenTitel icon={TrendUp} rechts={einzeln ? undefined : z.mieter}>{einzeln ? z.mieter : z.einheit}</KartenTitel>
            <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
              {a.geplanteAenderung ? <Chip ton="info">Geplanter Mietstand ab {deDatum(a.geplanteAenderung)} – Staffel/Index prüfen</Chip>
                : a.spielraum <= 0 ? <Chip ton="neutral">Kappungsgrenze erreicht</Chip>
                  : moeglich ? <Chip ton="ok">Erhöhung möglich</Chip> : <Chip ton="warn">Sperrfrist bis {deDatum(a.fruehestesVerlangen)}</Chip>}
            </div>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(min(170px,100%),1fr))', gap: 12 }}>
              {[
                ['Kaltmiete heute', eur(a.kaltAktuell), a.kaltProQm !== null ? `${eur(a.kaltProQm)} / m²` : 'ohne Fläche'],
                ['Letzte Änderung', a.letzteAenderung ? deDatum(a.letzteAenderung) : '–', 'Kaltmiete'],
                ['Verlangen ab', deDatum(a.fruehestesVerlangen), `wirksam frühestens ${deDatum(a.fruehesteWirksamkeit)}`],
                ['Höchstens', eur(a.maxKalt), `+${eur(a.spielraum)} · Basis ${eur(a.basisKalt)} (${deDatum(a.basisDatum)})`],
              ].map(([l, w, sub]) => (
                <div key={l} className="flex flex-col" style={{ gap: 2 }}>
                  <span style={{ fontSize: 11, color: 'var(--ink3)' }}>{l}</span>
                  <span className="immo-zahl" style={{ fontSize: 17, fontWeight: 800 }}>{w}</span>
                  <span style={{ fontSize: 11, color: 'var(--ink3)' }}>{sub}</span>
                </div>
              ))}
            </div>
            {(z.onOeffnen || z.onBearbeiten) && (
              <div className="flex flex-wrap" style={{ gap: 8 }}>
                {z.onOeffnen && <ImmoButton groesse={36} onClick={z.onOeffnen}>Zur Einheit</ImmoButton>}
                {z.onBearbeiten && <ImmoButton groesse={36} icon={PencilSimple} onClick={z.onBearbeiten}>Neuen Mietstand anlegen</ImmoButton>}
              </div>
            )}
          </InnenKarte>
        );
      })}
      <span style={{ fontSize: 12, color: 'var(--ink3)' }}>
        Orientierung nach § 558 BGB aus den hinterlegten Mietständen. Erhöhungen nach Modernisierung (§ 559) oder Betriebskostenänderungen
        sind hier nicht unterscheidbar; der Mietspiegel (ortsübliche Vergleichsmiete) ist noch nicht hinterlegt.
      </span>
    </>
  );
}
